import { PauseIcon, PlayIcon, StopIcon } from '@heroicons/react/24/outline';
import { DM_ACTION_COLUMN, DmButton } from './DmControlPrimitives';
import { OneLineNotice } from './OneLineNotice';
import type { MonitoringState } from './dmControlUtils';
import { PatientRequestButtons } from './PatientRequestButtons';

/** The buttons, stacked at one width and centred across the column. */
const ACTION_STACK = `${DM_ACTION_COLUMN} flex flex-col items-stretch gap-2`;

export interface IdleActionsProps {
    status: MonitoringState;
    alignerChangePending: boolean;
    busy: boolean;
    /** The last refusal from DM, in a line kept free so it never moves the column. */
    error: string | null;
    onStart: () => void;
    onPause: () => void;
    onScheduleResume: () => void;
    onResumeNow: () => void;
    onStop: () => void;
    onRequestScan: () => void;
    onAlignerChange?: () => void;
}

function StatusButtons(props: IdleActionsProps) {
    const { status, busy } = props;
    if (status === 'not_started' || status === 'stopped') {
        return (
            <DmButton kind="startGhost" icon={PlayIcon} onClick={props.onStart}>
                Start monitoring
            </DmButton>
        );
    }
    const stop = (
        <DmButton kind="dangerGhost" icon={StopIcon} onClick={props.onStop}>
            Stop monitoring
        </DmButton>
    );
    if (status === 'active') {
        return (
            <>
                <DmButton kind="warningGhost" icon={PauseIcon} onClick={props.onPause}>
                    Pause monitoring
                </DmButton>
                {stop}
            </>
        );
    }
    return (
        <>
            <DmButton icon={PlayIcon} disabled={busy} loading={busy} onClick={props.onResumeNow}>
                {busy ? 'Resuming…' : 'Resume now'}
            </DmButton>
            <DmButton onClick={props.onScheduleResume}>Schedule resume</DmButton>
            {stop}
        </>
    );
}

// Home of the status column: the status actions, then the requests to the patient.
export function IdleActions(props: IdleActionsProps) {
    return (
        <div className="relative flex flex-col">
            <div className={ACTION_STACK}>
                <StatusButtons {...props} />
            </div>
            <div className={`${ACTION_STACK} mt-3 border-t border-gray-100 pt-3`}>
                <PatientRequestButtons
                    status={props.status}
                    alignerChangePending={props.alignerChangePending}
                    onRequestScan={props.onRequestScan}
                    onAlignerChange={props.onAlignerChange}
                />
            </div>
            {/* Under the buttons, in the gap above the next block, so it never adds height. */}
            <OneLineNotice
                message={props.error}
                fromDm
                className="absolute inset-x-0 top-full mt-1"
            />
        </div>
    );
}
